import { z } from 'zod';
import { AgentErrorCodeSchema } from './error-code.js';

// ---- v2 tool-call summaries -------------------------------------------------
//
// SECURITY (#2006, epic #2004, FR-8): these schemas are the ONLY shapes that
// describe a tool invocation on the wire. They are STRUCTURALLY incapable of
// carrying a raw row dump, secret, DSN, phone value, token, or provider
// payload: every field is a closed `.strict()` scalar plus bounded `.max()`
// string previews, and there is deliberately NO open `z.record`/`z.any`/
// `z.unknown` "result" field that could absorb such data. Anything the client
// sees about a tool call is a pre-redacted human summary, never raw arguments
// or raw results.

/**
 * @aiPurpose summary of a tool call as it STARTS, streamed on a
 *   `tool_call_started` v2 event so the UI can show "running X…".
 * @aiSecurity `argsSummary` is a SAFE, pre-redacted human description of the
 *   arguments (e.g. "China requests, last 30 days") — never the raw argument
 *   object. There is no field for raw args by construction.
 * @aiPii `argsSummary` must already have any PII (phones, names) redacted by
 *   the producer before it reaches this schema.
 */
export const AgentToolCallStartedSchema = z
  .object({
    callId: z.string(),
    tool: z.string(),
    version: z.string(),
    argsSummary: z.string().max(500).optional(),
  })
  .strict();

export type AgentToolCallStarted = z.infer<typeof AgentToolCallStartedSchema>;

// Fields common to both result variants: identity, timing, and a bounded
// pre-redacted preview string. Spread into each `.strict()` variant rather
// than shared via `.merge()` so the discriminated union sees plain objects.
const toolCallResultBase = {
  callId: z.string(),
  tool: z.string(),
  version: z.string(),
  durationMs: z.number().int().nonnegative(),
  resultPreview: z.string().max(2000).optional(),
};

/**
 * @aiPurpose successful-result variant of `tool_call_result`. Carries the ids
 *   of any artifacts it produced (the real data travels as typed
 *   `ArtifactSchema` payloads) and an optional row count.
 * @aiInvariant `artifactIds` reference artifacts delivered via separate
 *   `artifact_created` events; this schema never inlines artifact data. An OK
 *   result carries NO `errorCode` by construction (BR-3/FR-5).
 */
const AgentToolCallOkResultSchema = z
  .object({
    ...toolCallResultBase,
    status: z.literal('ok'),
    artifactIds: z.array(z.string()).max(20),
    rowCount: z.number().int().nonnegative().optional(),
  })
  .strict();

/**
 * @aiPurpose error-result variant of `tool_call_result`. An errored call
 *   produces no artifacts or rows, so it carries neither — only a REQUIRED
 *   typed `errorCode` the client can branch on.
 * @aiInvariant `errorCode` is mandatory (BR-3/FR-5): an error result MUST be
 *   actionable, never a bare "something failed". It is drawn from the
 *   agent-scoped `AgentErrorCodeSchema`, never the full `ErrorCode` enum.
 */
const AgentToolCallErrorResultSchema = z
  .object({
    ...toolCallResultBase,
    status: z.literal('error'),
    errorCode: AgentErrorCodeSchema,
  })
  .strict();

/**
 * @aiPurpose summary of a tool call's RESULT, streamed on a `tool_call_result`
 *   v2 event. A `status`-discriminated union: an OK result carries artifact ids
 *   and an optional row count but no error code; an error result carries a
 *   required typed error code but no artifacts/rows.
 * @aiSecurity STRUCTURALLY cannot carry a row dump or secret — there is no
 *   open record/any/unknown field. `resultPreview` is a bounded, pre-redacted
 *   string (max 2000) only. `rowCount` is a scalar, not the rows themselves.
 * @aiInvariant (BR-3/FR-5) an error result MUST carry a typed actionable
 *   `errorCode`; an OK result MUST NOT carry one.
 */
export const AgentToolCallResultSchema = z.discriminatedUnion('status', [
  AgentToolCallOkResultSchema,
  AgentToolCallErrorResultSchema,
]);

export type AgentToolCallResult = z.infer<typeof AgentToolCallResultSchema>;
